import { useState, useEffect } from 'react';
import { Trophy, Music, Video, Users, ExternalLink, Loader2, Check } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useLocation } from 'wouter';
import { apiRequest } from '@/lib/queryClient';
import { useToast } from '@/hooks/use-toast';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import type { LeagueData } from './hooks/use-league-chat';

interface MiniLeagueCardProps {
  leagueData: LeagueData;
  currentUserId?: string | null;
  className?: string;
}

export function MiniLeagueCard({ leagueData, currentUserId, className }: MiniLeagueCardProps) {
  const [, setLocation] = useLocation();
  const { toast } = useToast();
  const [showConfirm, setShowConfirm] = useState(false);
  const [isJoining, setIsJoining] = useState(false);
  const [membershipStatus, setMembershipStatus] = useState<'loading' | 'member' | 'not_member'>('loading');

  // Check membership when the card mounts
  useEffect(() => {
    if (!currentUserId) {
      setMembershipStatus('not_member');
      return;
    }
    let cancelled = false;

    const checkMembership = async () => {
      try {
        const result = await apiRequest('GET', `/api/leagues/${leagueData.leagueId}/is-member`);
        if (!cancelled) {
          setMembershipStatus(result.isMember ? 'member' : 'not_member');
        }
      } catch {
        if (!cancelled) setMembershipStatus('not_member');
      }
    };

    checkMembership();
    return () => { cancelled = true; };
  }, [currentUserId, leagueData.leagueId]);

  const handleView = () => {
    setLocation(`/leagues/${leagueData.leagueId}`);
  };

  const handleJoin = async () => {
    if (!leagueData.isPublic && leagueData.inviteCode) {
      setShowConfirm(true);
      return;
    }
    await doJoin();
  };

  const doJoin = async () => {
    setIsJoining(true);
    try {
      const result = await apiRequest('POST', `/api/leagues/${leagueData.leagueId}/join`,
        leagueData.inviteCode ? { inviteCode: leagueData.inviteCode } : undefined
      );

      if (result?.alreadyMember) {
        toast({ title: `You're already in ${leagueData.name}` });
      } else {
        toast({ title: `Joined ${leagueData.name}!` });
      }
      setMembershipStatus('member');
      setLocation(`/leagues/${leagueData.leagueId}`);
    } catch (err: any) {
      const msg = err?.message || 'Failed to join league';
      if (msg.toLowerCase().includes('banned')) {
        toast({ title: 'Cannot join', description: 'You have been banned from this league.', variant: 'destructive' });
      } else if (msg.toLowerCase().includes('full') || msg.toLowerCase().includes('capacity')) {
        toast({ title: 'League is full', description: 'No spots available right now.', variant: 'destructive' });
      } else if (msg.toLowerCase().includes('invite') || msg.toLowerCase().includes('code')) {
        toast({ title: 'Invite required', description: 'A valid invite code is needed to join this league.', variant: 'destructive' });
      } else {
        toast({ title: 'Could not join', description: msg, variant: 'destructive' });
      }
    } finally {
      setIsJoining(false);
      setShowConfirm(false);
    }
  };

  const TypeIcon = leagueData.type === 'video' ? Video : Music;
  const spotsLeft = leagueData.maxParticipants - leagueData.participants;
  const isFull = spotsLeft <= 0;
  const isMember = membershipStatus === 'member';
  const isLoading = membershipStatus === 'loading';
  const canJoin = !isFull && !isMember && !isLoading;

  return (
    <>
      <div className={`flex items-center gap-3 p-2 bg-muted/50 rounded-lg ${className || ''}`}>
        {/* League image */}
        <div
          className="w-12 h-12 flex-shrink-0 rounded overflow-hidden cursor-pointer"
          style={{ backgroundColor: leagueData.themeColor || '#5D3FD3' }}
          onClick={handleView}
        >
          {leagueData.headerImageUrl ? (
            <img
              src={leagueData.headerImageUrl}
              alt={leagueData.name}
              className="w-full h-full object-cover"
            />
          ) : (
            <div className="w-full h-full flex items-center justify-center">
              <Trophy className="h-6 w-6 text-white/80" />
            </div>
          )}
        </div>

        {/* League info */}
        <div className="flex-1 min-w-0 cursor-pointer" onClick={handleView}>
          <p className="font-medium text-sm truncate">{leagueData.name}</p>
          <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
            <TypeIcon className="h-3 w-3" />
            <Users className="h-3 w-3" />
            <span>{leagueData.participants}/{leagueData.maxParticipants}</span>
            {!leagueData.isPublic && (
              <span className="text-[10px] bg-muted px-1 rounded">Invite</span>
            )}
          </div>
        </div>

        {/* Actions */}
        <div className="flex gap-1 flex-shrink-0">
          {isLoading ? (
            <Button variant="ghost" size="sm" className="h-8 text-xs" disabled>
              <Loader2 className="h-3 w-3 animate-spin" />
            </Button>
          ) : isMember ? (
            <Button
              variant="ghost"
              size="sm"
              className="h-8 text-xs gap-1 text-muted-foreground"
              onClick={handleView}
            >
              <Check className="h-3 w-3" />
              Member
            </Button>
          ) : isFull ? (
            <Button variant="ghost" size="sm" className="h-8 text-xs text-muted-foreground" disabled>
              Full
            </Button>
          ) : canJoin ? (
            <Button
              variant="secondary"
              size="sm"
              className="h-8 text-xs"
              onClick={handleJoin}
              disabled={isJoining}
            >
              {isJoining ? <Loader2 className="h-3 w-3 animate-spin" /> : 'Join'}
            </Button>
          ) : (
            <Button
              variant="ghost"
              size="icon"
              className="h-8 w-8"
              onClick={handleView}
              title="View league"
            >
              <ExternalLink className="h-4 w-4" />
            </Button>
          )}
        </div>
      </div>

      {/* Double-confirm dialog for invite-only leagues */}
      <AlertDialog open={showConfirm} onOpenChange={setShowConfirm}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Join Private League</AlertDialogTitle>
            <AlertDialogDescription>
              <strong>{leagueData.name}</strong> is an invite-only league.
              Would you like to join?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isJoining}>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={doJoin} disabled={isJoining}>
              {isJoining ? 'Joining...' : 'Join League'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
